/*
 * @Description: 移动端导航
 * @Author: zhanjunwei
 * @Date: 2024-12-04 10:39:28
 * @LastEditors: zhanjunwei
 * @LastEditTime: 2025-02-28 16:07:29
 */
'use client';
import {
  useMobileLogin,
  useLocalGlobalStore,
  mobilePaths,
  handleLogout,
} from '@ssv-website/hooks';
import { EventBus } from '../../event-bus';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeftIcon } from 'tdesign-icons-react';
import { CosImage } from '../../cos-image';
import mobile_logo from './images/logo_mobile.png';
import mobile_logo_dark from './images/mobile-logo-dark.png';
import open_menu from './images/open-menu.png';
import close_menu from './images/close-menu.png';
import open_menu_dark from './images/open_menu_dark.png';
import close_menu_dark from './images/close_menu_dark.png';
import menu_bg_blue from './images/menu-bg-blue.svg';
import menu_bg_white from './images/menu-bg-white.svg';
import subMenu_arrow_dark from './images/sub-menu-arrow-dark.svg';
import subMenu_arrow_light from './images/sub-menu-arrow-light.svg';
import { LinkHref, menus } from '../config';
import { openConferenceChatEntry } from '../conferenceChat';

const ASSET_MAP = new Map<any, string>([
  [mobile_logo, 'web-ui/navbar/mobile/images/logo_mobile.png'],
  [mobile_logo_dark, 'web-ui/navbar/mobile/images/mobile-logo-dark.png'],
  [open_menu, 'web-ui/navbar/mobile/images/open-menu.png'],
  [close_menu, 'web-ui/navbar/mobile/images/close-menu.png'],
  [open_menu_dark, 'web-ui/navbar/mobile/images/open_menu_dark.png'],
  [close_menu_dark, 'web-ui/navbar/mobile/images/close_menu_dark.png'],
  [menu_bg_blue, 'web-ui/navbar/mobile/images/menu-bg-blue.svg'],
  [menu_bg_white, 'web-ui/navbar/mobile/images/menu-bg-white.svg'],
  [subMenu_arrow_dark, 'web-ui/navbar/mobile/images/sub-menu-arrow-dark.svg'],
  [subMenu_arrow_light, 'web-ui/navbar/mobile/images/sub-menu-arrow-light.svg'],
]);

const getAssetPath = (asset: any) => {
  const path = ASSET_MAP.get(asset);
  if (!path) {
    throw new Error('MobileNavbar: 未找到对应的 COS 图片路径');
  }
  return path;
};
interface IProps {
  rootClassName?: string;
  initType?: 'transparent' | 'white';
  mobileMode?: 'default' | 'embedded';
  className?: string;
  title?: string;
  hideEmbeddedTitle?: boolean;
  mobileMenuIcon?: React.ReactNode;
  mobileMenuButtonClassName?: string;
  // 自定义退出行为；不传则默认跳 /login
  onLogout?: () => void;
}
export const MobileNavbar = ({
  initType,
  mobileMode = 'default',
  className = '',
  title,
  hideEmbeddedTitle = false,
  mobileMenuIcon,
  mobileMenuButtonClassName,
  rootClassName = '',
  onLogout,
}: IProps) => {
  const [open, setOpen] = useState(false);
  const [mobileGoBack, setMobileGoBack] = useState<any>(null);
  const { is_login, userName } = useLocalGlobalStore((s) => s);
  const [visible, setVisible] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const onDisableScroll = (disable: boolean) => {
    if (boxRef.current && boxRef.current.parentElement) {
      boxRef.current.parentElement.style.overflowY = disable
        ? 'hidden'
        : 'scroll';
      document.body.style.overflow = disable ? 'hidden' : 'auto';
    }
  };

  const onClick = () => {
    setOpen(!open);
    onDisableScroll(!open);
  };

  useEffect(() => {
    const navbar = ref.current;
    const io = new IntersectionObserver(
      (en) => setVisible(en[0].intersectionRatio > 0),
      { root: null, rootMargin: '-6%' }
    );
    if (navbar) {
      io.observe(navbar);
    }
    return () => {
      io.disconnect();
    };
  }, []);

  const isWhiteType = initType === 'white';
  const isEmbedded = mobileMode === 'embedded';
  const mobileTitle = title || '会聊';

  const isDiaplay = mobileGoBack ? false : visible;

  const menu_icon = useMemo(() => {
    if (isWhiteType) {
      return open ? close_menu_dark : open_menu_dark;
    }

    if (open && isDiaplay) return close_menu;
    if (!open && isDiaplay) return open_menu;
    if (open && !isDiaplay) return close_menu_dark;
    if (!open && !isDiaplay) return open_menu_dark;
    return open_menu;
  }, [open, isDiaplay]);

  const logo = getTypeMap(
    isWhiteType,
    isDiaplay,
    mobile_logo,
    mobile_logo_dark
  );

  const cs_menu_box = open ? 'h-screen' : 'h-0';
  const cs_postion = isEmbedded
    ? `fixed bg-white/100 top-0 py-[1px] ${isWhiteType ? '' : 'bg-transparent'}`
    : isDiaplay
    ? 'absolute top-11'
    : 'fixed bg-white/100 top-0 py-[1px]';

  const onLink = (link?: string) => {
    if (link) {
      setOpen(false);
      onDisableScroll(false);
      window.location.href = link;
    }
  };

  return (
    <div
      ref={boxRef}
      className={`absolute left-0 w-full ${
        isEmbedded ? 'h-12' : 'h-24'
      } z-[99] hidden mobile:block ${rootClassName}`}
    >
      <div
        ref={ref}
        className={`h-12 relative left-0 w-full bg-transparent ${
          isEmbedded ? 'top-0' : 'top-11'
        }`}
      />

      <div
        className={`w-full box-border fixed left-0 z-50 ${cs_postion} ${className}`}
      >
        <div className="overflow-hidden relative px-5 h-12">
          <div className="h-12 flex items-center justify-between">
            <div className="flex items-center gap-x-2">
              <CosImage
                fallbackSrc={logo}
                path={getAssetPath(logo)}
                alt=""
                className="h-[1.5rem] w-[6.25rem] object-contain"
              />
              {!isEmbedded && is_login && (
                <div
                  className={`w-px h-5 ${
                    isWhiteType ? 'bg-primary' : 'bg-white'
                  }`}
                />
              )}
              {isEmbedded ? (
                !hideEmbeddedTitle && (
                  <span
                    className={`text-sm leading-none font-medium line-clamp-1 ${
                      isWhiteType ? 'text-black' : 'text-white'
                    }`}
                  >
                    {mobileTitle}
                  </span>
                )
              ) : (
                <span
                  className={`text-ms leading-none line-clamp-1 ${
                    isWhiteType ? ' text-black ' : 'text-white'
                  }`}
                >
                  {userName}
                </span>
              )}
            </div>
            {mobileMenuIcon ? (
              <button
                type="button"
                onClick={onClick}
                aria-label={open ? '关闭菜单' : '打开菜单'}
                className={
                  mobileMenuButtonClassName ??
                  'w-12 h-12 rounded-full flex items-center justify-center'
                }
              >
                {mobileMenuIcon}
              </button>
            ) : (
              <CosImage
                onClick={onClick}
                fallbackSrc={menu_icon}
                path={getAssetPath(menu_icon)}
                alt=""
                className="w-12 h-12 rounded-full"
              />
            )}
          </div>
        </div>
        {mobileGoBack && !open && (
          <div className="h-10 bg-white items-center hidden mobile:flex">
            <div
              className="px-5"
              onClick={() => {
                window.location.href = mobileGoBack.link;
              }}
            >
              <ChevronLeftIcon size={16} />
            </div>
            <div className="h-[14px] w-[1px] bg-[#353F49]/20" />
            <div className="text-xs text-[#353F49]/60 px-5">
              {mobileGoBack.title}
            </div>
          </div>
        )}
      </div>

      <MenuList
        visible={isDiaplay}
        cssMenuBox={cs_menu_box}
        initType={initType}
        onLink={onLink}
        onLogout={onLogout}
      />
      <EventBus.MobileRouteOn callback={setMobileGoBack} />
    </div>
  );
};

const getTypeMap = (
  isWhite: boolean,
  isVis: boolean,
  light: any,
  dark: any
) => {
  if (isWhite) return dark;
  return isVis ? light : dark;
};

interface IList {
  visible: boolean;
  cssMenuBox?: string;
  initType?: IProps['initType'];
  onLink: (link?: string) => void;
  onLogout?: () => void;
}
const MenuList = ({
  visible,
  cssMenuBox,
  initType,
  onLink,
  onLogout,
}: IList) => {
  const initFlags = Array(menus.length).fill(false);
  const [flags, setFlags] = useState(initFlags);
  const {
    is_login,
    currentIdentity: { teamType },
    hasTeam,
  } = useLocalGlobalStore((s) => s);
  const loginUrl = useMobileLogin({
    coralAppId: process.env.NEXT_PUBLIC_CORAL_APPID as string,
  });
  const openSubMenu = (index: number) => {
    setFlags(flags.map((v, i) => (i === index ? !v : v)));
  };
  const isWhiteType = initType === 'white';
  const bg = getTypeMap(isWhiteType, visible, menu_bg_blue, menu_bg_white);
  const cssText = getTypeMap(isWhiteType, visible, 'text-white', 'text-black');
  const borderColor = getTypeMap(isWhiteType, visible, '#4096FB', '#F5F5F5');
  const handleLogo = () => {
    window.location.href = loginUrl;
  };

  const goAccount = () => {
    // 1 个人 2团队
    const url = teamType === 2 ? LinkHref.TeamAccount : LinkHref.Account;
    window.location.href = url;
  };

  const authList = getAuthList(teamType, hasTeam);
  return (
    <div
      className={`fixed overflow-hidden flex flex-col w-full transition-all duration-700 top-0 left-0 z-10 ${cssMenuBox}`}
    >
      <div className="w-full flex flex-col flex-1 overflow-y-scroll mt-[6rem] box-border pr-[1.87rem]">
        {!is_login && (
          <div className={`text-right font-400 text-2xl pt-8  ${cssText}`}>
            <div className={`text-right font-400 text-2xl  ${cssText}`}>
              <a onClick={handleLogo}>登录</a>
            </div>
          </div>
        )}
        {menus
          .filter((v) => !v.mobileHide)
          .map((item, index) => (
            <div
              key={index}
              className={`relative font-400 text-2xl ${cssText}`}
            >
              <div
                className="flex justify-end items-center"
                onClick={() => {
                  if (item.subMenus && !item.href) {
                    openSubMenu(index);
                  }
                }}
              >
                {item.subMenus && (
                  <div
                    onClick={() => openSubMenu(index)}
                    className="flex-1 flex justify-end h-full pt-8 items-center"
                  >
                    <CosImage
                      fallbackSrc={getTypeMap(
                        isWhiteType,
                        visible,
                        subMenu_arrow_light,
                        subMenu_arrow_dark
                      )}
                      path={getAssetPath(
                        getTypeMap(
                          isWhiteType,
                          visible,
                          subMenu_arrow_light,
                          subMenu_arrow_dark
                        )
                      )}
                      alt=""
                      className="w-3 h-3 mr-4"
                    />
                  </div>
                )}
                <span className="pt-8" onClick={() => onLink(item.href)}>
                  {item.title}
                </span>
              </div>
              {item.subMenus && (
                <div
                  className={`overflow-hidden duration-500 flex flex-col gap-5 ${
                    flags[index] ? 'h-fit pt-5' : 'h-0 pt-0'
                  }`}
                >
                  {item.subMenus?.map((v, i) => (
                    <div
                      className="text-right text-base opacity-80 font-400"
                      key={i}
                      onClick={() => onLink(v.href)}
                    >
                      {v.title}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

        {is_login && (
          <>
            <div
              className={`border-y-[1px] ml-[1.78rem] mt-8 pb-8`}
              style={{ borderColor: borderColor }}
            >
              {authList.map((el) => (
                <div
                  key={el.title}
                  className={`relative font-400 text-2xl pt-8  ${cssText}`}
                >
                  <div className="flex justify-end items-center">
                    <span onClick={el.click ?? goAccount}>{el.title}</span>
                  </div>
                </div>
              ))}
            </div>
            <div
              className={`relative font-400 text-2xl pt-8 border-b-[1px] pb-8 mb-8 ml-[1.78rem] ${cssText}`}
              style={{ borderColor: borderColor }}
            >
              <div className="flex justify-end items-center">
                <span onClick={onLogout ?? handleLogout}>退出</span>
              </div>
            </div>
          </>
        )}
      </div>
      <CosImage
        fallbackSrc={bg}
        path={getAssetPath(bg)}
        alt=""
        className="w-full h-screen object-cover absolute left-0 top-0 -z-10 "
      />
    </div>
  );
};

const getAuthList = (teamType?: number, hasTeam?: boolean) => {
  const list: { title: string; click?: () => void }[] = [{ title: '账号中心' }];
  // 1 个人 2团队
  if (teamType === 2 || hasTeam) {
    list.push({
      title: '切换团队',
      click: () => {
        window.location.href = mobilePaths({}).teamPath;
      },
    });
  }
  list.push({
    title: '会聊',
    // 移动端个人中心区域只在登录后渲染，会聊入口进入前仍实时鉴权。
    click: () => void openConferenceChatEntry(),
  });
  return list;
};
